import { FC, useCallback } from 'react';
import { Button, ButtonProps } from 'antd';
import { useFormContext } from 'react-hook-form';
import { useAlgorithmFormValues } from '../useAlgorithmFormValues';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AutoJoinButton.translations.json';
import { AlgorithmJoinItemValidFormDataType } from './schema/types';
import { objectWithRandomUniqueKey } from '../../../common/utils/randomUniqueKey';

const { addTranslations, useTranslation } = i18nNsFactory('auto-join-button');
addTranslations(translations);

export interface AutoJoinButtonProps
  extends Omit<ButtonProps, 'id' | 'children'> {
  joinIndex: number;
}

export const AutoJoinButton: FC<AutoJoinButtonProps> = (props) => {
  const { joinIndex, ...rest } = props;
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const [algorithmJoins, algorithmSourceId] = useAlgorithmFormValues([
    'joins',
    `joins.${joinIndex}.algorithmSourceId`,
  ] as const);

  const [algorithmSourcesByIds] = AlgorithmJoinsFormStore.useValue(
    'algorithmSourcesByIds',
  );

  const autoJoin = useCallback(() => {
    const thisAlgorithmSource = algorithmSourcesByIds.get(algorithmSourceId);

    if (thisAlgorithmSource?.sourceType === 'DB') {
      throw new Error('Auto-join is not supported for DB sources');
    }

    const closestPrecedingAlgorithmJoinWithSourceBasedOnSameDataObjectDescription =
      algorithmJoins
        .slice(0, joinIndex)
        .reverse()
        .find((join) => {
          const algorithmSource = algorithmSourcesByIds.get(
            join.algorithmSourceId,
          );

          return (
            algorithmSource &&
            algorithmSource.sourceType !== 'DB' &&
            algorithmSource.sourceCode === thisAlgorithmSource?.sourceCode
          );
        });
    const closestPrecedingAlgorithmSourceBasedOnSameDataObjectDescription =
      closestPrecedingAlgorithmJoinWithSourceBasedOnSameDataObjectDescription &&
      algorithmSourcesByIds.get(
        closestPrecedingAlgorithmJoinWithSourceBasedOnSameDataObjectDescription.algorithmSourceId,
      );

    if (!closestPrecedingAlgorithmSourceBasedOnSameDataObjectDescription) {
      throw new Error(
        'No preceding algorithm join with the same data object description-based source found',
      );
    }

    const autoJoinedItems = thisAlgorithmSource?.dimensions.reduce<
      AlgorithmJoinItemValidFormDataType[]
    >((items, targetDimension) => {
      const sourceDimension =
        closestPrecedingAlgorithmSourceBasedOnSameDataObjectDescription.dimensions.find(
          (d) => d.code === targetDimension.code,
        );

      if (!sourceDimension) {
        return items;
      }

      return items.concat(
        objectWithRandomUniqueKey({
          _variant: 'DIMENSION_DIMENSION' as const,
          algorithmSourceId:
            closestPrecedingAlgorithmSourceBasedOnSameDataObjectDescription[
              'id'
            ],
          sourceDimension: sourceDimension.code,
          targetDimension: targetDimension.code,
        }),
      );
    }, []);

    if (autoJoinedItems?.length) {
      setValue(`joins.${joinIndex}.algorithmJoinItems`, autoJoinedItems, {
        shouldDirty: true,
      });
    }
  }, [
    algorithmJoins,
    algorithmSourceId,
    algorithmSourcesByIds,
    joinIndex,
    setValue,
  ]);

  return (
    <Button
      id={`algorithmJoin-control-${joinIndex}-auto-join`}
      onClick={autoJoin}
      {...rest}
    >
      {t('auto-join')}
    </Button>
  );
};
